import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowRight, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { blogPosts } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { faqs } from '../data/mock';

const Resources = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'ISO 9001', 'ISO 45001', 'ISO 14001', 'HSE'];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="resources-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="page-title">{t.resources.title}</h1>
          <p className="page-subtitle">
            {t.resources.subtitle}
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="blog-filters-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder={t.resources.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        <div className="category-filters">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category === 'all' ? t.resources.all : category}
            </button>
          ))}
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="blog-posts-section">
        <div className="blog-grid">
          {filteredPosts.map((post) => (
            <article key={post.id} className="blog-card">
              <div className="blog-card-header">
                <span className="blog-category">{post.category}</span>
              </div>
              <h3 className="blog-title">{language === 'en' ? post.titleEn : post.title}</h3>
              <p className="blog-excerpt">{language === 'en' ? post.excerptEn : post.excerpt}</p>
              <div className="blog-meta">
                <div className="meta-item">
                  <Calendar size={16} />
                  <span>{new Date(post.date).toLocaleDateString('it-IT')}</span>
                </div>
                <div className="meta-item">
                  <Clock size={16} />
                  <span>{post.readTime}</span>
                </div>
                <div className="meta-item">
                  <User size={16} />
                  <span>{post.author}</span>
                </div>
              </div>
              <Link to={`/risorse/${post.slug}`} className="blog-read-more">
                {t.resources.readArticle} <ArrowRight size={16} />
              </Link>
            </article>
          ))}
        </div>
        {filteredPosts.length === 0 && (
          <div className="no-results">
            <p>{t.resources.noResults}</p>
          </div>
        )}
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-header">
          <h2 className="section-title">{t.resources.faqTitle}</h2>
          <p className="section-description">
            {t.resources.faqSubtitle}
          </p>
        </div>
        <div className="faq-container">
          <Accordion type="single" collapsible className="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="faq-question">
                  {language === 'en' ? faq.questionEn : faq.question}
                </AccordionTrigger>
                <AccordionContent className="faq-answer">
                  {language === 'en' ? faq.answerEn : faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section-small">
        <div className="cta-content">
          <h2 className="cta-title">{t.resources.ctaTitle}</h2>
          <p className="cta-description">
            {t.resources.ctaSubtitle}
          </p>
          <Link to={language === 'en' ? '/contact' : '/contatti'} className="btn-primary">
            {t.nav.contact}
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Resources;
